import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import axios from "axios";
import config from "../../config";
import { useMasterPassword } from "../../components/MasterPasswordDialog/MasterPasswordContext";

const fetchOrderInvoice = (orderId) => {
    const params = {
        form_name: `payment-order`,
        doc_name: orderId,
    };
    return axios.post(
        `method/frappe_helper.frappe_helper.doctype.desk_form.desk_form.get_form_data`,
        params
    );
};

export const useOrderInvoiceData = (orderId, onSuccess, onError) => {
    return useQuery(
        ["OrderInvoiceData", orderId],
        async () => {
            const { data } = await fetchOrderInvoice(orderId);
            return data.message;
        },
        {
            enabled: !!orderId,
        }
    );
};

const createOrderInvoice = (orderId, formData, masterPassword) => {
    const params = {
        model: `Table Order`,
        name: orderId,
        method: `make_invoice`,
        args: JSON.stringify(formData),
    };
    
    if (masterPassword) {
        params.master_password = masterPassword;
    }
    
    try {
        return axios.post(
            masterPassword
                ? `method/restaurant_management.api.escalate_privilege`
                : `method/frappe_helper.api.call`,
            params
        );
    } catch (err) {
        throw new err();
    }
};

export const useCrateOrderInvoice = () => {
    const { showMasterPasswordDialog } = useMasterPassword();
    const queryClient = useQueryClient();
    
    const mutation = useMutation(
        async ({ orderId, formData, floorName, masterPassword }) => {
            const data = await createOrderInvoice(orderId, formData, masterPassword);
            return data;
        },
        {
            onSettled: (data, error, variables, context) => {
                const { orderId, floorName } = variables;
                queryClient.invalidateQueries([
                    "getFloorFullDetails",
                    floorName,
                ]);
                queryClient.invalidateQueries(["getTableOrderItems", orderId]);
            },
        }
    );

    const mutate = async (params, options = {}) => {
        const { elevatedPrivileges, elevatedText, ...otherParams } = params;

        if (elevatedPrivileges) {
            try {
                const masterPassword = await showMasterPasswordDialog({
                    elevatedText,
                });

                return mutation.mutate(
                    { ...otherParams, masterPassword },
                    options
                );
            } catch (error) {
                console.log("Master password dialog cancelled");
                return;
            }
        } else {
            return mutation.mutate(otherParams, options);
        }
    };

    return {
        ...mutation,
        mutate,
    };
};

const fetchInvoicePrintData = (invoiceId) => {
    let token = localStorage.getItem("accessToken");
    const myHeaders = new Headers();
    myHeaders.append("Authorization", token);

    const params = new URLSearchParams({
        model: "POS Invoice",
        doctype: "POS Invoice",
        name: invoiceId,
        no_letterhead: 1,
        letterhead: "No Letterhead",
        settings: JSON.stringify({}),
        format: "POS",
        _lang: "en",
    });

    const requestOptions = {
        method: "GET",
        headers: myHeaders,
    };

    const backUrl = config.baseUrl;
    const url = `${backUrl}/api/method/frappe.utils.print_format.download_pdf?${params}`;

    try {
        return fetch(url, requestOptions);
    } catch (err) {
        throw err;
    }
};

export const useOrderInvoicePrintData = (invoiceId, onSuccess, onError) => {
    const queryClient = useQueryClient();

    return useQuery(
        ["OrderInvoicePrintData", invoiceId],
        async () => {
            const response = await fetchInvoicePrintData(invoiceId);
            const blob = await response.blob();
            return URL.createObjectURL(blob);
        },
        {
            onSuccess: (data) => {
                queryClient.refetchQueries(["OrderInvoiceData", invoiceId]);

                if (onSuccess) onSuccess();
            },
            refetchOnmount: true,
            onError,
            enabled: !!invoiceId,
        }
    );
};


const fetchTodayClosedInvoices = () => {
    const params = {
        fields: JSON.stringify(["*"]),
        filters: JSON.stringify([["posting_date", "Timespan", "Today"]]),
        limit: 0, // Set the limit to 0 to load all items
    };

    return axios.get(`/resource/POS Invoice`, { params });
};

export const useTodayClosedInvoices = (onSuccess, onError) => {
    return useQuery(
        ["getTodayClosedInvoices"],
        async () => {
            const data = await fetchTodayClosedInvoices();
            return data.data.data;
        },
        {
            onSuccess,
            onError,
            cacheTime: 30 * 1000 * 60 * 60 * 24,
        }
    );
};

const fetchClosedInvoicesPage = async ({
    mode = "today",
    limit = 50,
    offset = 0,
    search = "",
    status,
}) => {
    const filters = [];
    if (mode === "today") {
        filters.push(["posting_date", "Timespan", "Today"]);
    }
    if (status) {
        filters.push(["status", "=", status]);
    }

    const q = String(search || "").trim();
    const orFilters = [];
    if (q) {
        // Server-side search across common invoice fields
        const like = `%${q}%`;
        orFilters.push(["name", "like", like]);
        orFilters.push(["customer", "like", like]);
        orFilters.push(["customer_name", "like", like]);
    }

    const params = {
        fields: JSON.stringify(["*"]),
        filters: JSON.stringify(filters),
        limit_page_length: limit,
        limit_start: offset,
        order_by: "posting_date desc, posting_time desc",
    };
    if (orFilters.length) {
        params.or_filters = JSON.stringify(orFilters);
    }

    const res = await axios.get(`/resource/POS Invoice`, { params });
    return res.data.data || [];
};

/**
 * Paginated list of POS Invoices with Today/All mode and optional server-side search.
 * Uses infinite pagination; consumers call fetchNextPage() to load more.
 */
export const useClosedInvoices = ({
    mode = "today",
    limit = 50,
    search = "",
    status,
    enabled = true,
} = {}) => {
    return useInfiniteQuery({
        queryKey: ["closedInvoices", mode, limit, search, status],
        queryFn: async ({ pageParam = 0 }) => {
            return fetchClosedInvoicesPage({
                mode,
                limit,
                offset: pageParam,
                search,
                status,
            });
        },
        getNextPageParam: (lastPage, allPages) => {
            if (!Array.isArray(lastPage)) return undefined;
            if (lastPage.length < limit) return undefined;
            return allPages.length * limit;
        },
        enabled: !!enabled,
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        cacheTime: 5 * 60 * 1000,
    });
};
